import React, { useCallback, useState } from "react";
import {
  Popover,
  Button,
  Typography,
  Box,
  ClickAwayListener,
} from "@mui/material";
import useDemandResponseContext from "../../context/useDemandResponseContext";
import StyledPopover from "../../../components/StyledComponents/Popover/Popover";
import {
  BodyLarge,
  H7TitleLarge,
  H9TitleSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import { StyledIconButton } from "../../../components/StyledComponents/Buttons/AuradineButtons";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { format, set } from "date-fns";
import dayjs from "dayjs";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { useDeletePowerCurtail } from "../../queries/useDeleteCurtailSched.ts";
import { GetActiveCurtailmentSitesList } from "../../../api/api.js";
import { capitalizeString } from "../../../components/util.js";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

export const SchedulePopover = (props) => {
  const {
    eventToDelete,
    setEventToDelete,
    setSchedulePopOverOpen,
    schedulePopOverOpen,
    eventModalAnchor,
    handleClose,
  } = props;
  const { colors } = useTheme();
  return (
    <StyledPopover
      colors={colors}
      id="schedule-popover"
      open={schedulePopOverOpen}
      anchorEl={eventModalAnchor}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "right",
      }}
      PaperProps={{
        style: { minWidth: "43rem" },
      }}
    >
      test popover
    </StyledPopover>
  );
};

const EventPopover = (props) => {
  const { refetch: refetchCurtailData, userRole } = props;
  const { colors } = useTheme();
  const {
    eventModalAnchor,
    setEventModalAnchor,
    eventModalData,
    setEventModalData,
    setOpenPowerCurtailOperationModal,
    setEditModeOn,
    calendarEvents,
    setCalendarEvents,
    openDeleteRecurringEventDialog,
    setOpenDeleteRecurringEventDialog,
    setEventIdToDelete,
  } = useDemandResponseContext();
  const { setToastMessage, setToastOpen, setActiveSiteCurtailCount } =
    useAppContextProvider();
  const { getAccessTokenSilently } = useAuth0()

  const open = Boolean(eventModalAnchor);
  const id = open ? "simple-popover" : undefined;
  const handleClose = () => {
    setEventModalAnchor(null);
    setEventModalData([]);
  };
  const handleEdit = () => {
    setEventModalAnchor(null);
    setEditModeOn(true);
    setOpenPowerCurtailOperationModal(true);
  };
  const { siteSelectedName } = useDemandResponseContext();
  const { mutate: deleteCurtailEvent } =
    useDeletePowerCurtail();

  const handleDelete = (event, eventId, eventModalData) => {
    event.stopPropagation();
    if (eventModalData && eventModalData.recurring) {
      setOpenDeleteRecurringEventDialog(true);
      setEventIdToDelete(eventId);
    } else {
      deleteCurtailEvent(
        {
          id: eventId,
          siteName: siteSelectedName,
        },
        {
          onSuccess: async (data) => {
            if (data?.status === "ok") {
              setEventModalAnchor(null);
              setToastMessage(
                "Curtail event deleted successfully. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab "
              );
              setToastOpen(true);
              await refetchCurtailData();
              const authToken = await getAccessTokenSilently();
              const curtailData = await GetActiveCurtailmentSitesList(authToken);
              if (curtailData?.activeCurtailSchedule?.length) {
                setActiveSiteCurtailCount(curtailData.activeCurtailSchedule.length);
              } else {
                setActiveSiteCurtailCount(0);
              }
            } else {
              setEventModalAnchor(null);
              setToastMessage(
                "Curtail event deletion failed"
              );
              setToastOpen(true);
            }
          },
          onError: (error) => {
            setEventModalAnchor(null);
            setToastMessage(
              "Curtail event deletion failed"
            );
            setToastOpen(true);
          },
        }
      );
    }
  };

  const returnWhenData = useCallback(
    (eventModalData) => {
      let dateFormattedString = "";
      if (eventModalData.length === 0)
        return dateFormattedString;
      else {
        const date = new Date(eventModalData?.start);
        const formattedDate = format(
          date,
          "EEEE, MMMM d, yyyy"
        );
        const startTime = format(
          eventModalData?.start,
          "h:mm a"
        );
        const endTime = format(
          eventModalData?.end,
          "h:mm a"
        );
        dateFormattedString = `${formattedDate}, ${startTime} - ${endTime}`;
      }
      return dateFormattedString;
    },
    [eventModalData]
  );

  const whenData = returnWhenData(eventModalData);

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <>
          <StyledPopover
            colors={colors}
            id={id}
            open={open}
            anchorEl={eventModalAnchor}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            PaperProps={{
              style: { minWidth: "43rem" },
            }}
          >
            <Box
              display={"grid"}
              gridTemplateRows={"auto auto"}
              className={"event-popover-content"}
              rowGap={6}
            >
              <Box
                display={"grid"}
                gridTemplateColumns={"0.75fr 0.25fr"}
                gap={3}
                className={"event-popover-header"}
              >
                <Box
                  display={"grid"}
                  gridTemplateColumns={"0.05fr 0.95fr"}
                  gap={2}
                  className={"event-popover-header-left"}
                  justifyContent={"start"}
                  alignItems={"center"}
                >
                  <Box
                    style={{
                      width: "1rem",
                      height: "1rem",
                      borderRadius: "50%",
                      backgroundColor:
                        eventModalData?.color,
                    }}
                  ></Box>
                  <H7TitleLarge>
                    {eventModalData?.id}
                  </H7TitleLarge>
                </Box>
                <Box
                  display={"grid"}
                  gridTemplateColumns={"0.75fr 0.25fr"}
                  gap={4}
                >
                  <Box
                    display={"grid"}
                    gridTemplateColumns={"auto auto"}
                    justifyContent={"end"}
                    gap={2}
                    justifySelf={"stretch"}
                  >
                    {/* <StyledIconButton
                      disabled={
                        eventModalData?.start < dayjs()
                      }
                      onClick={handleEdit}
                    >
                      <EditRoundedIcon />
                    </StyledIconButton> */}
                    <StyledIconButton 
                      colors={colors}
                      onClick={(event) =>
                        handleDelete(
                          event,
                          eventModalData.id,
                          eventModalData
                        )
                      }
                      disabled={
                        // dayjs(eventModalData?.end).isBefore(
                        //   dayjs()
                        // ) ||
                        userRole === "user" ||
                        eventModalData?.source?.toLowerCase() ===
                          "voltus" ||
                        eventModalData?.source?.toLowerCase() ===
                          "cpower"
                      }
                    >
                      <span className="material-symbols-outlined">
                        delete
                      </span>
                    </StyledIconButton>
                  </Box>
                  <StyledIconButton colors={colors} onClick={handleClose}>
                    <CloseRoundedIcon />
                  </StyledIconButton>
                </Box>
              </Box>
              <Box
                className={"event-popover-body"}
                display={"grid"}
                gridAutoRows={"auto"}
                rowGap={4}
              >
                <Box
                  display={"grid"}
                  gridTemplateColumns={"0.2fr 0.8fr"}
                  className="when-data"
                >
                  <H9TitleSmall>When</H9TitleSmall>
                  <BodyLarge>{whenData}</BodyLarge>
                </Box>
                <Box
                  display={"grid"}
                  gridTemplateColumns={"0.2fr 0.8fr"}
                  className="power-data"
                >
                  <H9TitleSmall>Power</H9TitleSmall>
                  <BodyLarge>
                    {eventModalData?.target_power_usage} {eventModalData?.per_miner_target_power ? "W" : "MW"}
                  </BodyLarge>
                </Box>
                <Box
                  display={"grid"}
                  gridTemplateColumns={"0.2fr 0.8fr"}
                  className="source-data"
                >
                  <H9TitleSmall>Source</H9TitleSmall>
                  <BodyLarge>
                    {capitalizeString(
                      eventModalData?.source
                    )}
                  </BodyLarge>
                </Box>
                <Box
                  display={"grid"}
                  gridTemplateColumns={"0.2fr 0.8fr"}
                  className="source-data"
                >
                  <H9TitleSmall>Type</H9TitleSmall>
                  <BodyLarge>
                    {capitalizeString(
                      eventModalData?.eventType
                    )}
                  </BodyLarge>
                </Box>
                <Box
                  display={"grid"}
                  gridTemplateColumns={"0.2fr 0.8fr"}
                  className="source-data"
                >
                  <H9TitleSmall>Email</H9TitleSmall>
                  <BodyLarge>
                    {eventModalData?.email}
                  </BodyLarge>
                </Box>
              </Box>
            </Box>
          </StyledPopover>
        </>
      </ClickAwayListener>
    </>
  );
};

export default EventPopover;
